import type { Plan, PlanUpdateRequest } from '@cfa/pathway-fe-shared/xp';

import type { useUpdatePlan } from '@/hooks/useUpdatePlan';

import { getLanguageCode } from '@/utils/language';

type PlanDescriptionAction = 'added' | 'updated' | 'cleared';

const getPlanDescriptionAction = (
  saved: string,
  current: string,
): PlanDescriptionAction => {
  if (!saved && current) return 'added';
  if (saved && !current) return 'cleared';
  return 'updated';
};

interface UseSavePlanDescriptionOptions {
  plan: Plan | undefined;
  isReadOnly: boolean | undefined;
  userLanguage: string;
  updateDescription: ReturnType<typeof useUpdatePlan>['updateDescription'];
  isPlanUpdatePending: boolean;
  descriptionValue: string;
  savedPlanDescription: string;
  onDescriptionSaved: (result: {
    wasCleared: boolean;
    action: PlanDescriptionAction;
  }) => void;
}

export interface SavePlanDescriptionResult {
  isClearing: boolean;
  isSaveDisabled: boolean;
  onSave: () => Promise<void>;
}

export const useSavePlanDescription = ({
  plan,
  isReadOnly,
  userLanguage,
  updateDescription,
  isPlanUpdatePending,
  descriptionValue,
  savedPlanDescription,
  onDescriptionSaved,
}: UseSavePlanDescriptionOptions): SavePlanDescriptionResult => {
  const trimmedDescription = descriptionValue.trim();
  const trimmedSavedDescription = savedPlanDescription.trim();
  const isClearing =
    trimmedDescription.length === 0 && trimmedSavedDescription.length > 0;
  const isSaveDisabled =
    (isReadOnly ?? false) ||
    trimmedDescription === trimmedSavedDescription ||
    isPlanUpdatePending;

  const onSave = async () => {
    if (!plan || isSaveDisabled) {
      return;
    }

    const langKey = getLanguageCode(userLanguage);

    const nextDescription: NonNullable<PlanUpdateRequest['description']> = {
      ...(plan.description ?? {}),
      [langKey]: trimmedDescription,
    };

    await updateDescription(plan, nextDescription);

    onDescriptionSaved({
      wasCleared: trimmedDescription.length === 0,
      action: getPlanDescriptionAction(
        trimmedSavedDescription,
        trimmedDescription,
      ),
    });
  };

  return {
    isClearing,
    isSaveDisabled,
    onSave,
  };
};
